import config from 'config'

// PostHog only starts once analytics consent is in place (see utils/consent).
// Until then `$posthog` is null, so the `this.$posthog?.capture(...)` call sites
// across the app are no-ops and nothing is stored in the browser.
//
// posthog-js is a quarter of the main bundle, and every public page downloads
// and runs that bundle before it can paint. It loads as its own chunk instead,
// after the page is up. While that chunk is on its way, `$posthog` is a stand-in
// that queues the calls, and they replay in order once PostHog is initialised,
// so nothing captured in those first moments is lost (the same as when the
// library sat in the main bundle).
let client = null
let loading = null
const queued = []

const standIn = {
  capture: (...args) => queued.push(['capture', args]),
  identify: (...args) => queued.push(['identify', args]),
  reset: (...args) => queued.push(['reset', args]),
  opt_out_capturing: (...args) => queued.push(['opt_out_capturing', args])
}

export function enablePosthog() {
  if (client || loading || !config.posthog_api_key) return

  // No chunk name on purpose: a nondescript file name keeps the chunk clear of
  // the content-filter lists that match the vendor's name in script URLs.
  loading = import('posthog-js')
    .then(({ default: posthog }) => {
      posthog.init(`${config.posthog_api_key}`, {
        api_host: 'https://eu.i.posthog.com',
        defaults: '2025-05-24',
        person_profiles: 'identified_only'
      })

      // A signed-in user is identified before the queue replays, so queued
      // captures carry the user id. The profile store identifies too, once it
      // has loaded; identifying with the same id twice is a no-op.
      if (config.user?.id) posthog.identify(config.user.id)

      queued.splice(0).forEach(([method, args]) => posthog[method](...args))
      client = posthog
    })
    .catch(() => {
      // Blocked or offline: analytics stays off for this page view.
      loading = null
      queued.length = 0
    })
}

export function posthogClient() {
  return client || (loading ? standIn : null)
}

// Vue plugin
const PostHogPlugin = {
  install(Vue) {
    Object.defineProperty(Vue.prototype, '$posthog', {
      get: () => posthogClient()
    })
  }
}

export default PostHogPlugin
